import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import * as interventionsApi from "../../api/interventions";
import { Intervento } from "../../api/interventions";
import { CentroAssistenzaOption } from "../../api/anagrafica";
import { getErrorMessage } from "../../api/client";
import { DeviceFisicoAutocomplete } from "./DeviceFisicoAutocomplete";
import { useToast } from "../../context/ToastContext";
import { Button } from "./Button";
import { Input } from "./Input";
import { SedeAutocomplete } from "./SedeAutocomplete";
import { CampiOrario } from "./CampiOrario";
import { orarioPerInput } from "../../utils/orario";
import { AssegnatarioSelect } from "./AssegnatarioSelect";
import { VigilanzaCongiunta, payloadVigilanza, sceltaDaRecord } from "./VigilanzaCongiunta";
import {
  RiferimentiOdoo,
  riferimentiOdooDaRecord,
  payloadRiferimentiOdoo,
  usaPuoGestireRiferimentiOdoo,
} from "./RiferimentiOdoo";

/** yyyy-MM-dd per gli <input type="date">, o stringa vuota se la data manca. */
function perInput(iso: string | null | undefined): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : "";
}

/** La decisione salvata nella forma della tendina: `""` = da decidere. */
function annullamentoPerSelect(v: boolean | null | undefined): string {
  return v === true ? "true" : v === false ? "false" : "";
}

/**
 * Modifica dei dati operativi di un intervento dal pannello di dettaglio, che
 * finora era di sola lettura: gli interventi arrivavano da Odoo e non erano
 * toccabili dall'app.
 *
 * Lo stato usa un campo con suggerimenti (datalist) invece di un <select>
 * chiuso: `stato` è testo libero, popolato dagli stage configurati in Odoo, e
 * un elenco fisso finirebbe per non combaciare con quelli reali. Così i valori
 * già in uso si scelgono con un tocco, ma se ne può scrivere uno nuovo.
 */
export function InterventoModifica({
  intervento,
  centri,
  onAggiornato,
}: {
  intervento: Intervento;
  /**
   * Elenco dei Centri Assistenza per la riassegnazione. Omesso (undefined) il
   * campo non compare: la riassegnazione è riservata ad admin/staff, e il
   * backend ignora comunque centro_assistenza_id per chiunque altro.
   */
  centri?: CentroAssistenzaOption[];
  onAggiornato: (record: Intervento) => void;
}) {
  const { showToast } = useToast();
  // Il titolo si modifica solo sui record locali: su quelli collegati a Odoo la
  // sync lo riscrive a ogni giro, quindi il campo non viene nemmeno mostrato —
  // il backend rifiuterebbe comunque la modifica.
  const isLocale = !intervento.odoo_model || !intervento.odoo_id;
  const [nome, setNome] = useState(intervento.nome ?? "");
  const [stato, setStato] = useState(intervento.stato ?? "");
  const [centroId, setCentroId] = useState(intervento.centro_assistenza_id ?? "");
  const [tecnico, setTecnico] = useState(intervento.tecnico ?? "");
  const [dataPrevista, setDataPrevista] = useState(perInput(intervento.data_prevista));
  const [dataCompletamento, setDataCompletamento] = useState(perInput(intervento.data_completamento));
  // `orarioPerInput` e non `perInput`: le colonne orario arrivano come
  // "1970-01-01T09:30:00.000Z" e passarle da `new Date()` sposterebbe l'ora del
  // fuso di chi guarda. Si affetta la stringa. Vedi utils/orario.ts.
  const [orarioInizio, setOrarioInizio] = useState(orarioPerInput(intervento.orario_inizio));
  const [orarioFine, setOrarioFine] = useState(orarioPerInput(intervento.orario_fine));
  const [note, setNote] = useState(intervento.note ?? "");
  const [inGaranzia, setInGaranzia] = useState(intervento.in_garanzia ?? false);
  // Vigilanza congiunta: la spunta, l'istituto scelto (o ALTRO) e il nome
  // scritto a mano. È da qui che si taggano gli interventi congiunti già
  // esistenti — alla creazione la spunta c'era già, l'istituto no.
  const [richiedeVigilanza, setRichiedeVigilanza] = useState(intervento.richiede_vigilanza ?? false);
  const [istitutoScelta, setIstitutoScelta] = useState(
    sceltaDaRecord(intervento.istituto_vigilanza_congiunto_id, intervento.istituto_vigilanza_congiunto_nome)
  );
  const [istitutoAltro, setIstitutoAltro] = useState(intervento.istituto_vigilanza_congiunto_nome ?? "");
  // Il pezzo fisico. Il testo e la chiave viaggiano insieme: vedi il commento
  // sulla proprietà della chiave in DeviceFisicoAutocomplete.
  const [deviceTesto, setDeviceTesto] = useState(intervento.device_matricola ?? "");
  const [deviceId, setDeviceId] = useState(intervento.device_id ?? "");
  // Sede: fino a ieri si poteva scegliere solo alla creazione, quindi un
  // intervento nato senza — il caso normale per quelli importati da Odoo —
  // restava scollegato dall'anagrafica per sempre.
  const [sedeId, setSedeId] = useState(intervento.sede_id ?? "");
  const [sedeNome, setSedeNome] = useState(intervento.sede_nome ?? "");
  const [assegnato, setAssegnato] = useState(intervento.assegnato_utente_id ?? "");
  // I cinque riferimenti OdA/OdV, nella forma del form (garanzia come stringa a
  // tre stati). Chi li vede lo decide l'hook: admin e staff, non i Centri
  // Assistenza né gli operatori tecnici, che pure possono modificare un
  // intervento.
  const puoGestireOrdini = usaPuoGestireRiferimentiOdoo();
  const [riferimenti, setRiferimenti] = useState(riferimentiOdooDaRecord(intervento));
  // Se un annullamento si fattura: decisione commerciale, quindi stesso hook dei
  // riferimenti OdA/OdV (admin e staff). Compare solo sugli annullati, e a dirlo
  // e' la `causa` calcolata dal backend — nessuna copia qui dell'elenco degli
  // stati annullati. Lo stato si legge da quello SALVATO: se lo si cambia nel
  // pannello, la tendina compare dopo il salvataggio.
  const mostraAnnullamento = puoGestireOrdini && (intervento.fatturabile?.causa?.startsWith("annullato") ?? false);
  const [annullamento, setAnnullamento] = useState(annullamentoPerSelect(intervento.annullamento_fatturabile));
  const [statiNoti, setStatiNoti] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Riallinea i campi quando si apre un altro intervento senza smontare il
  // pannello (es. arrivando da un altro link ?open=).
  useEffect(() => {
    setNome(intervento.nome ?? "");
    setStato(intervento.stato ?? "");
    setTecnico(intervento.tecnico ?? "");
    setDataPrevista(perInput(intervento.data_prevista));
    setDataCompletamento(perInput(intervento.data_completamento));
    setOrarioInizio(orarioPerInput(intervento.orario_inizio));
    setOrarioFine(orarioPerInput(intervento.orario_fine));
    setNote(intervento.note ?? "");
    setInGaranzia(intervento.in_garanzia ?? false);
    setRichiedeVigilanza(intervento.richiede_vigilanza ?? false);
    setIstitutoScelta(
      sceltaDaRecord(intervento.istituto_vigilanza_congiunto_id, intervento.istituto_vigilanza_congiunto_nome)
    );
    setIstitutoAltro(intervento.istituto_vigilanza_congiunto_nome ?? "");
    setDeviceTesto(intervento.device_matricola ?? "");
    setDeviceId(intervento.device_id ?? "");
    setCentroId(intervento.centro_assistenza_id ?? "");
    setAssegnato(intervento.assegnato_utente_id ?? "");
    setSedeId(intervento.sede_id ?? "");
    setSedeNome(intervento.sede_nome ?? "");
    setRiferimenti(riferimentiOdooDaRecord(intervento));
    setAnnullamento(annullamentoPerSelect(intervento.annullamento_fatturabile));
  }, [intervento.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    interventionsApi
      .getStatiInterventi()
      .then(setStatiNoti)
      .catch(() => setStatiNoti([])); // i suggerimenti sono un extra: se mancano, il campo resta libero
  }, []);

  const nomeModificato = isLocale && nome.trim() !== (intervento.nome ?? "");
  const sedeModificata = sedeId !== (intervento.sede_id ?? "");
  const modificato =
    nomeModificato ||
    sedeModificata ||
    stato !== (intervento.stato ?? "") ||
    tecnico !== (intervento.tecnico ?? "") ||
    dataPrevista !== perInput(intervento.data_prevista) ||
    dataCompletamento !== perInput(intervento.data_completamento) ||
    orarioInizio !== orarioPerInput(intervento.orario_inizio) ||
    orarioFine !== orarioPerInput(intervento.orario_fine) ||
    note !== (intervento.note ?? "") ||
    inGaranzia !== (intervento.in_garanzia ?? false) ||
    richiedeVigilanza !== (intervento.richiede_vigilanza ?? false) ||
    istitutoScelta !==
      sceltaDaRecord(intervento.istituto_vigilanza_congiunto_id, intervento.istituto_vigilanza_congiunto_nome) ||
    istitutoAltro.trim() !== (intervento.istituto_vigilanza_congiunto_nome ?? "") ||
    deviceId !== (intervento.device_id ?? "") ||
    (centri !== undefined && centroId !== (intervento.centro_assistenza_id ?? "")) ||
    assegnato !== (intervento.assegnato_utente_id ?? "") ||
    // Senza questa riga il bottone "Salva modifiche" resterebbe spento dopo aver
    // scritto un numero d'ordine, e la modifica sembrerebbe rifiutata. Il
    // confronto e' su tutti e cinque i campi insieme, come oggetto: cinque
    // confronti separati sono cinque occasioni di dimenticarne uno.
    (puoGestireOrdini &&
      JSON.stringify(riferimenti) !== JSON.stringify(riferimentiOdooDaRecord(intervento))) ||
    (mostraAnnullamento && annullamento !== annullamentoPerSelect(intervento.annullamento_fatturabile));

  async function salva() {
    if (nomeModificato && !nome.trim()) {
      showToast("Il titolo non può essere vuoto.", "error");
      return;
    }

    setIsSaving(true);
    try {
      const { record } = await interventionsApi.updateIntervento(intervento.id, {
        // Inviato solo se davvero cambiato: su un record Odoo il backend
        // risponderebbe 400 anche ricevendo il valore identico a quello attuale.
        ...(nomeModificato ? { nome: nome.trim() } : {}),
        // Anche la sede solo se cambiata: rimandarla identica farebbe
        // riscrivere al backend anche cliente_id, che la segue — sovrascrivendo
        // un'azienda madre magari corretta a mano.
        ...(sedeModificata ? { sede_id: sedeId || null } : {}),
        stato,
        tecnico,
        data_prevista: dataPrevista || null,
        data_completamento: dataCompletamento || null,
        orario_inizio: orarioInizio || null,
        orario_fine: orarioFine || null,
        in_garanzia: inGaranzia,
        // Le tre colonne insieme, con la stessa regola del form di creazione.
        ...payloadVigilanza(richiedeVigilanza, istitutoScelta, istitutoAltro),
        // Solo la chiave, non il testo: la matricola scritta a mano senza
        // sceglierla dalla tendina non collega niente, ed e' giusto cosi' —
        // qui il campo libero non ha un posto dove essere salvato, a
        // differenza delle attivazioni.
        device_id: deviceId || null,
        note,
        // Inviato solo se il campo è visibile: per chi non può riassegnare non
        // ha senso mandare un valore che il backend scarterebbe comunque.
        ...(centri !== undefined ? { centro_assistenza_id: centroId || null } : {}),
        // Stesso criterio per i cinque riferimenti OdA/OdV, e solo i CAMBIATI:
        // fattura e garanzia sono di un ordine che può coprire altre attività
        // (vedi `payloadRiferimentiOdoo`).
        ...(puoGestireOrdini ? payloadRiferimentiOdoo(riferimenti, riferimentiOdooDaRecord(intervento)) : {}),
        // Solo se la tendina e' visibile: su un intervento non annullato non si
        // tocca la decisione salvata.
        ...(mostraAnnullamento
          ? { annullamento_fatturabile: annullamento === "" ? null : annullamento === "true" }
          : {}),
        assegnato_utente_id: assegnato || null,
      });
      showToast("Intervento aggiornato.", "success");
      onAggiornato(record);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aggiornare l'intervento."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {isLocale && (
        <Input
          label="Titolo"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          hint="Come l'intervento compare in elenco. Modificabile perché questo record non arriva da Odoo."
        />
      )}

      {/* Modificabile anche sui record Odoo, al contrario del titolo: la sync
          non scrive mai sede_id, quindi non c'è nulla che possa sovrascrivere
          questa scelta al giro successivo. */}
      <SedeAutocomplete
        label="Sede"
        value={sedeNome}
        onChange={setSedeNome}
        sedeId={sedeId}
        onSedeIdChange={setSedeId}
        soloSelezione
        placeholder="Cerca una sede in anagrafica…"
      />

      <div>
        <label htmlFor="intervento-stato" className="mb-1 block text-sm font-medium text-ink/70">
          Stato
        </label>
        <input
          id="intervento-stato"
          list="intervento-stati-noti"
          value={stato}
          onChange={(e) => setStato(e.target.value)}
          placeholder="Es. Completato"
          className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-primary"
        />
        <datalist id="intervento-stati-noti">
          {statiNoti.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <p className="mt-1 text-xs text-ink/40">Scegli fra quelli già in uso o scrivine uno nuovo.</p>
      </div>

      <AssegnatarioSelect
        value={assegnato}
        onChange={setAssegnato}
        cliente_id={intervento.cliente_id}
        centro_assistenza_id={centroId || intervento.centro_assistenza_id}
        aiuto="Chi se ne occupa. Il campo Tecnico resta testo libero, per i nomi che arrivano da Odoo."
      />

      {centri !== undefined && (
        <div>
          <label htmlFor="intervento-centro" className="mb-1 block text-sm font-medium text-ink/70">
            Centro Assistenza
          </label>
          <select
            id="intervento-centro"
            value={centroId}
            onChange={(e) => setCentroId(e.target.value)}
            className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-primary"
          >
            <option value="">Non assegnato</option>
            {centri.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ink/40">
            Cambiando centro, l'intervento passa a chi lo vedrà e potrà lavorarci.
          </p>
        </div>
      )}

      <Input label="Tecnico" value={tecnico} onChange={(e) => setTecnico(e.target.value)} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label="Data prevista"
          type="date"
          value={dataPrevista}
          onChange={(e) => setDataPrevista(e.target.value)}
        />
        <Input
          label="Data completamento"
          type="date"
          value={dataCompletamento}
          onChange={(e) => setDataCompletamento(e.target.value)}
        />
      </div>

      {/* Riferito alla data PREVISTA qui sopra — l'appuntamento, non il
          completamento. Sui record collegati a Odoo resta comunque
          modificabile: la sync non scrive gli orari, che nel database di Odoo
          non hanno un corrispondente. */}
      <CampiOrario
        inizio={{ value: orarioInizio, onChange: (e) => setOrarioInizio(e.target.value) }}
        fine={{ value: orarioFine, onChange: (e) => setOrarioFine(e.target.value) }}
      />

      <DeviceFisicoAutocomplete
        label="Cassaforte"
        value={deviceTesto}
        onChange={setDeviceTesto}
        deviceId={deviceId}
        onDeviceIdChange={setDeviceId}
        sedeId={sedeId || null}
        hint={
          sedeId
            ? "I pezzi che stanno in questa sede compaiono per primi."
            : "Scegli prima la sede per vedere subito la cassaforte che sta lì."
        }
      />

      {/* Sta accanto alle date e non fra le note perché è un dato di lavoro:
          decide se l'intervento finisce in fattura. La spiegazione è nella
          riga sotto perché "in garanzia" da solo non dice la conseguenza, che
          è la cosa per cui la casella esiste. */}
      <label className="flex items-start gap-2 rounded-lg border border-line bg-ink/[0.02] px-3 py-2.5 text-sm text-ink">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-black/20"
          checked={inGaranzia}
          onChange={(e) => setInGaranzia(e.target.checked)}
        />
        <span>
          <span className="font-medium">Intervento in garanzia</span>
          {/* Sugli annullati la decisione sull'annullamento vince sulla garanzia
              (vedi `fatturabilitaIntervento`): il testo solito prometterebbe un
              «No» che l'export non scrive. A dire che è annullato è la `causa`
              calcolata dal backend, come per la tendina più sotto. */}
          {intervento.fatturabile?.causa?.startsWith("annullato") ? (
            <span className="text-ink/50">
              {" "}
              — l'intervento è annullato: per la fatturazione conta la decisione sull'annullamento, non la garanzia.
            </span>
          ) : (
            <span className="text-ink/50"> — escluso dalla fatturazione. Nell'export compare come «Fatturabile: No».</span>
          )}
        </span>
      </label>

      {/* Subito sotto la spunta "in garanzia" e con `distinguiDaInGaranzia`
          accesa: sono due garanzie diverse nello stesso pannello — quella
          dell'intervento e quella dell'ordine di vendita — e senza dirlo si
          confondono. */}
      {puoGestireOrdini && (
        <RiferimentiOdoo
          idPrefisso="intervento-modifica"
          distinguiDaInGaranzia
          ordini={intervento.ordini_odoo}
          valori={riferimenti}
          onChange={setRiferimenti}
        />
      )}

      {/* Tre valori e non una spunta, per la stessa ragione di «OdV in
          garanzia»: «Da decidere» non è «No». */}
      {mostraAnnullamento && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="intervento-annullamento-fatturabile" className="text-sm font-medium text-ink">
            Annullamento fatturabile
          </label>
          <select
            id="intervento-annullamento-fatturabile"
            value={annullamento}
            onChange={(e) => setAnnullamento(e.target.value)}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          >
            <option value="">Da decidere</option>
            <option value="true">Sì, da fatturare</option>
            <option value="false">No, non fatturabile</option>
          </select>
          <p className="text-xs text-ink/50">
            L'intervento è annullato: si fattura solo se lo decidi qui (di norma, quando l'annullamento è a carico del
            cliente). «Da decidere» non conta fra i da fatturare.
          </p>
        </div>
      )}

      <VigilanzaCongiunta
        richiede={richiedeVigilanza}
        onRichiedeChange={setRichiedeVigilanza}
        scelta={istitutoScelta}
        onSceltaChange={setIstitutoScelta}
        altro={istitutoAltro}
        onAltroChange={setIstitutoAltro}
        congiuntoCon={intervento.congiunto_con}
      />

      <div>
        <label htmlFor="intervento-note" className="mb-1 block text-sm font-medium text-ink/70">
          Note
        </label>
        <textarea
          id="intervento-note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-primary"
        />
      </div>

      {/* Gli interventi sincronizzati restano allineati a Odoo al prossimo
          sync: meglio dirlo qui che lasciar sparire una modifica senza
          spiegazione. */}
      {intervento.odoo_synced && (
        <p className="text-xs text-ink/50">
          Questo intervento è collegato a Odoo: stato e data prevista possono essere riallineati ai valori di Odoo alla
          prossima sincronizzazione. Il titolo, per lo stesso motivo, si corregge in Odoo e non da qui.
        </p>
      )}

      <div className="flex justify-end">
        <Button type="button" size="sm" loading={isSaving} disabled={!modificato} onClick={salva}>
          <Check className="h-4 w-4" aria-hidden="true" />
          Salva modifiche
        </Button>
      </div>
    </div>
  );
}
